import { ReactNode } from 'react';
import { useRoutes, Navigate } from 'react-router-dom';

import { EditMyAccountMenu } from '../modules/edit-my-account/EditMyAccountMenu';

import AuthenticatedWrapper from './AuthenticatedWrapper';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import Root from './Root';
import ROUTES from './routes';

import { Page404 } from '@/components';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { MainRecoverEmail, Login } from '@/modules/auth';
import { ChangeTactic } from '@/modules/build-team/ChangeTactic';
import { Recommender } from '@/modules/build-team/Recommender';
import { Team11 as BuildTeam11 } from '@/modules/build-team/Team11';
import { Team5 as BuildTeam5 } from '@/modules/build-team/Team5';
import { TeamConfirm } from '@/modules/build-team/TeamConfirm';
import { TeamConfirmF5 } from '@/modules/build-team/TeamConfirmF5';
import { WelcomeF5 } from '@/modules/build-team/WelcomeF5';
import { Summary } from '@/modules/dashboard';
import { RecentMatchStats } from '@/modules/dashboard/RecentMatchStats';
import { EditMyAccountLayout } from '@/modules/edit-my-account/EditMyAccountLayout';
import { MyAwards } from '@/modules/edit-my-account/MyAwards';
import { MyCampaigns } from '@/modules/edit-my-account/MyCampaigns';
import MyCbu from '@/modules/edit-my-account/MyCbu';
import MyPersonalData from '@/modules/edit-my-account/MyPersonalData';
import { MyTeam } from '@/modules/edit-my-account/MyTeam';
import { ShieldPanel } from '@/modules/edit-my-account/ShieldPanel';
import { TshirtPanel } from '@/modules/edit-my-account/TshirtPanel';
import { Faqs } from '@/modules/faqs/Faqs';
import { MyAccount } from '@/modules/my-account';
import { AccountMap } from '@/modules/my-account/AccountMap';
import { PaymentCard } from '@/modules/my-account/PaymentCard';
import { PaymentForm } from '@/modules/my-account/PaymentForm';
import { PaymentMethods } from '@/modules/my-account/PaymentMethods';
import { MobilePremiumBenefits } from '@/modules/my-account/PremiumBenefits';
import { AccountMovements } from '@/modules/my-account/components/AccountMovements';
import MyMedals from '@/modules/my-medals';
import { Team11 as MyTeam11 } from '@/modules/my-team/Team11';
import { Team5 as MyTeam5 } from '@/modules/my-team/Team5';
import { NotificationsMobile } from '@/modules/notifications';
import RankingPage from '@/modules/ranking';
import {
  RegistrationSchool,
  RegistrationNameTeam,
  Registration,
  Welcome,
} from '@/modules/registration';
import { Challenge } from '@/modules/tourneys/challenge';
import { HomeTDA } from '@/modules/tourneys/friends';
import { CreateTournamentTDA } from '@/modules/tourneys/friends/CreateTournamentTDA';
import InviteToFriendsTournament from '@/modules/tourneys/friends/InviteToFriendsTournament';
import { ViewAdminTda } from '@/modules/tourneys/friends/ViewAdmin';
import { ViewStatistics } from '@/modules/tourneys/friends/ViewStatistics';
import { ViewTourney } from '@/modules/tourneys/friends/ViewTourney';
import { CreateTeamFCup } from '@/modules/tourneys/friendsCup/CreateTeam';
import { InfoFCup } from '@/modules/tourneys/friendsCup/Info';
import InviteFCup from '@/modules/tourneys/friendsCup/InviteToFriendsCup';
import { ViewAdminFCup } from '@/modules/tourneys/friendsCup/ViewAdmin';
import { ViewTeamFCup } from '@/modules/tourneys/friendsCup/ViewTeam';
import { HomeFCup } from '@/modules/tourneys/friendsCup/index';
import { GoldDateHome } from '@/modules/tourneys/goldDate';
import { GoldDateRanking } from '@/modules/tourneys/goldDate/GoldDateRanking';
import { InfoGoldDate } from '@/modules/tourneys/goldDate/InfoGoldDate';
import { MyGoldDateList } from '@/modules/tourneys/goldDate/MyGoldDateList';
import MLpage from '@/modules/tourneys/minileagues';
import { AdminMinileague } from '@/modules/tourneys/minileagues/AdminML';
import { CreateML } from '@/modules/tourneys/minileagues/CreateML';
import { InfoML } from '@/modules/tourneys/minileagues/InfoML';
import InviteToFriendsMinileagues from '@/modules/tourneys/minileagues/InviteToFriendsMinileagues';
import { ReactivateMinileague } from '@/modules/tourneys/minileagues/Reactivate';
import { ViewML } from '@/modules/tourneys/minileagues/ViewML';
import { ViewRivalML } from '@/modules/tourneys/minileagues/ViewRivalML';
import { ViewFinalML } from '@/modules/tourneys/minileagues/viewFinalML';
import { TDEpage } from '@/modules/tourneys/school';
import HomeTDAxPrizes from '@/modules/tourneys/tdaxprizes';
import ViewTapTourney from '@/modules/tourneys/tdaxprizes/ViewTapTourney';
import { TriviaSectionMobile } from '@/modules/trivia/TriviaSection';
import { EnumContextTourneys } from '@/types/team';

export interface PartialRouteObject {
  children?: PartialRouteObject[];
  element?: ReactNode;
  path?: string;
}

const privateRoutes: PartialRouteObject[] = [
  {
    path: '*',
    element: <Navigate replace to={ROUTES.summary} />,
  },
  {
    path: ROUTES.summary,
    children: [
      {
        path: ROUTES.summary,
        element: <PrivateRoute element={<Summary />} />,
      },
    ],
  },
  {
    path: ROUTES.recentMatchStats,
    children: [
      {
        path: ROUTES.recentMatchStats,
        element: <PrivateRoute element={<RecentMatchStats />} />,
      },
    ],
  },
  {
    path: ROUTES.welcome,
    children: [
      {
        path: ROUTES.welcome,
        element: <PrivateRoute element={<Welcome />} />,
      },
    ],
  },
  {
    path: ROUTES.welcomeF5,
    children: [
      {
        path: ROUTES.welcomeF5,
        element: <PrivateRoute element={<WelcomeF5 />} />,
      },
    ],
  },
  {
    path: ROUTES.registration,
    children: [
      {
        path: ROUTES.registration,
        element: <PrivateRoute element={<Registration />} />,
      },
      {
        path: ROUTES.registrationSchool,
        element: <PrivateRoute element={<RegistrationSchool />} />,
      },
      {
        path: ROUTES.registrationNameTeam,
        element: <PrivateRoute element={<RegistrationNameTeam />} />,
      },
    ],
  },
  {
    path: ROUTES.buildTeam,
    children: [
      {
        path: ROUTES.buildTeam11,
        element: <PrivateRoute element={<BuildTeam11 />} />,
      },
      {
        path: ROUTES.teamConfirm,
        element: <PrivateRoute element={<TeamConfirm />} />,
      },
      {
        path: ROUTES.changeTactic,
        element: <PrivateRoute element={<ChangeTactic />} />,
      },
      {
        path: ROUTES.buildTeam5,
        element: <PrivateRoute element={<BuildTeam5 />} />,
      },
      {
        path: ROUTES.teamConfirmF5,
        element: <PrivateRoute element={<TeamConfirmF5 />} />,
      },
    ],
  },
  {
    path: ROUTES.myTeam,
    children: [
      {
        path: ROUTES.myTeam11,
        element: <PrivateRoute element={<MyTeam11 />} />,
      },
      {
        path: ROUTES.myTeam5,
        element: <PrivateRoute element={<MyTeam5 />} />,
      },
    ],
  },
  {
    path: ROUTES.recommender,
    element: <PrivateRoute element={<Recommender />} />,
  },
  {
    path: ROUTES.myAccount,
    children: [
      {
        path: ROUTES.myAccount,
        element: <PrivateRoute element={<MyAccount />} />,
      },
      {
        path: ROUTES.premiumBenefits,
        element: <PrivateRoute element={<MobilePremiumBenefits />} />,
      },
      {
        path: ROUTES.virtualCharges,
        element: <PrivateRoute element={<AccountMap />} />,
      },
      {
        path: ROUTES.paymentMethodSelection,
        element: <PrivateRoute element={<PaymentMethods />} />,
      },
      {
        path: ROUTES.selectPaymentCard,
        element: <PrivateRoute element={<PaymentCard />} />,
      },
      {
        path: ROUTES.paymentForm,
        element: <PrivateRoute element={<PaymentForm />} />,
      },
    ],
  },
  {
    path: ROUTES.editAccount.home,
    element: <PrivateRoute element={<EditMyAccountLayout />} />,
    children: [
      {
        path: '',
        element: <PrivateRoute element={<MyPersonalData />} />,
      },
      {
        path: ROUTES.editAccount.myData,
        element: <PrivateRoute element={<MyPersonalData />} />,
      },
      {
        path: ROUTES.editAccount.menu,
        element: <PrivateRoute element={<EditMyAccountMenu />} />,
      },
      {
        path: ROUTES.editAccount.myTeam,
        element: <PrivateRoute element={<MyTeam />} />,
      },
      {
        path: ROUTES.editAccount.shieldPanel,
        element: <PrivateRoute element={<ShieldPanel />} />,
      },
      {
        path: ROUTES.editAccount.tshirtPanel,
        element: <PrivateRoute element={<TshirtPanel />} />,
      },
      {
        path: ROUTES.editAccount.myAwards,
        element: <PrivateRoute element={<MyAwards />} />,
      },
      {
        path: ROUTES.editAccount.myCBU,
        element: <PrivateRoute element={<MyCbu />} />,
      },
      {
        path: ROUTES.editAccount.myHistory,
        element: <PrivateRoute element={<MyCampaigns />} />,
      },
    ],
  },
  {
    path: ROUTES.friendsTournament,
    children: [
      {
        path: ROUTES.friendsTournament,
        element: <PrivateRoute element={<HomeTDA />} />,
      },
      {
        path: ROUTES.createFriendsTournament,
        element: <PrivateRoute element={<CreateTournamentTDA />} />,
      },
      {
        path: ROUTES.reactivateFriendsTournament,
        element: (
          <PrivateRoute
            element={
              <CreateTournamentTDA context={EnumContextTourneys.Reactivate} />
            }
          />
        ),
      },
      {
        path: ROUTES.viewTourney,
        element: <PrivateRoute element={<ViewTourney />} />,
      },
      {
        path: ROUTES.adminTourney,
        element: <PrivateRoute element={<ViewAdminTda />} />,
      },
      {
        path: ROUTES.friendsTournamentsStadistics,
        element: <PrivateRoute element={<ViewStatistics />} />,
      },
      {
        path: ROUTES.InviteToFriendsTournament,
        children: [
          {
            path: ROUTES.InviteToFriendsTournament,
            element: <PrivateRoute element={<InviteToFriendsTournament />} />,
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.friendsTournamentPrizes,
    children: [
      {
        path: ROUTES.friendsTournamentPrizes,
        element: <PrivateRoute element={<HomeTDAxPrizes />} />,
      },
      {
        path: ROUTES.viewTapTourney,
        element: <PrivateRoute element={<ViewTapTourney />} />,
      },
    ],
  },
  {
    path: ROUTES.schoolTournament,
    children: [
      {
        path: ROUTES.primarySchoolTournament,
        element: <PrivateRoute element={<TDEpage />} />,
      },
    ],
  },
  {
    path: ROUTES.minileagues.home,
    children: [
      {
        path: ROUTES.minileagues.home,
        element: <PrivateRoute element={<MLpage />} />,
      },
      {
        path: ROUTES.minileagues.info,
        element: <PrivateRoute element={<InfoML />} />,
      },
      {
        path: ROUTES.minileagues.create,
        element: <PrivateRoute element={<CreateML />} />,
      },
      {
        path: ROUTES.minileagues.view,
        element: <PrivateRoute element={<ViewML />} />,
      },

      {
        path: ROUTES.minileagues.viewRival,
        element: <PrivateRoute element={<ViewRivalML />} />,
      },
      {
        path: ROUTES.minileagues.viewFinal,
        element: <PrivateRoute element={<ViewFinalML />} />,
      },
      {
        path: ROUTES.minileagues.invite,
        element: <PrivateRoute element={<InviteToFriendsMinileagues />} />,
      },
      {
        path: ROUTES.minileagues.reactivate,
        element: <PrivateRoute element={<ReactivateMinileague />} />,
      },
      {
        path: ROUTES.minileagues.admin,
        element: <PrivateRoute element={<AdminMinileague />} />,
      },
    ],
  },
  {
    path: ROUTES.friendsCup.home,
    children: [
      {
        path: ROUTES.friendsCup.home,
        element: <PrivateRoute element={<HomeFCup />} />,
      },
      {
        path: ROUTES.friendsCup.createTeam,
        element: <PrivateRoute element={<CreateTeamFCup />} />,
      },
      {
        path: ROUTES.friendsCup.info,
        element: <PrivateRoute element={<InfoFCup />} />,
      },
      {
        path: ROUTES.friendsCup.viewTeam,
        element: <PrivateRoute element={<ViewTeamFCup />} />,
      },
      {
        path: ROUTES.friendsCup.adminTeam,
        element: <PrivateRoute element={<ViewAdminFCup />} />,
      },
      {
        path: ROUTES.friendsCup.invite,
        children: [
          {
            path: ROUTES.friendsCup.invite,
            element: <PrivateRoute element={<InviteFCup />} />,
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.challenge.home,
    element: <PrivateRoute element={<Challenge />} />,
  },
  {
    path: ROUTES.goldDate.home, // InfoGoldDate
    children: [
      {
        path: ROUTES.goldDate.home,
        element: <PrivateRoute element={<GoldDateHome />} />,
      },
      {
        path: ROUTES.goldDate.list,
        element: <PrivateRoute element={<MyGoldDateList />} />,
      },
      {
        path: ROUTES.goldDate.ranking,
        element: <PrivateRoute element={<GoldDateRanking />} />,
      },
      {
        path: ROUTES.goldDate.info,
        element: <PrivateRoute element={<InfoGoldDate />} />,
      },
    ],
  },
  {
    path: ROUTES.movements,
    element: <PrivateRoute element={<AccountMovements />} />,
  },
  {
    path: ROUTES.trivia,
    element: <PrivateRoute element={<TriviaSectionMobile />} />,
  },
  {
    path: ROUTES.ranking,
    element: <PrivateRoute element={<RankingPage />} />,
  },
  {
    path: ROUTES.medals,
    element: <PrivateRoute element={<MyMedals />} />,
  },
  {
    path: ROUTES.notifications,
    element: <PrivateRoute element={<NotificationsMobile />} />,
  },
];

const publicRoutes: PartialRouteObject[] = [
  { path: ROUTES.basePath, element: <PublicRoute element={<Login />} /> },
  {
    path: ROUTES.recoverEmail,
    element: <PublicRoute element={<MainRecoverEmail />} />,
  },
  { path: ROUTES.notFound, element: <Page404 /> },
  {
    path: ROUTES.preguntasFrecuentes,
    element: <PublicRoute element={<Faqs />} />,
  },
];

const AppRouter = () => {
  useScrollToTop();
  const routing = useRoutes([
    {
      path: ROUTES.basePath,
      children: [
        {
          element: <Root />,
          children: [
            {
              element: <AuthenticatedWrapper />,
              children: [
                {
                  children: [...privateRoutes],
                },
                {
                  children: [...publicRoutes],
                },
              ],
            },
          ],
        },
      ],
    },
  ]);

  return routing;
};

export default AppRouter;
